import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axiosClient';
import { useTheme } from '../../context/ThemeContext';
import { useBookingDraft } from '../../context/PortalContext';
import toast from '../../utils/toast';
import { motion } from 'framer-motion';
import ProfileAvatar from '../../components/shared/ProfileAvatar';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

const BookingReviewPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const { draft, clearDraft } = useBookingDraft();
  const publicBookingId = (() => {
    try { return localStorage.getItem('public_booking_id'); } catch { return null; }
  })();
  
  const [barber, setBarber] = useState(null);
  const [service, setService] = useState(null);
  const [salonSettings, setSalonSettings] = useState(null);
  const [notes, setNotes] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [confirmationPayload, setConfirmationPayload] = useState(null);

  // Redirect if booking data is incomplete
  useEffect(() => {
    if (!draft.barber_user_id || !draft.service_id || !draft.start_local) {
      navigate('/app/book');
      return;
    }
  }, [draft, navigate]);

  // Load barber, service, and salon settings
  useEffect(() => {
    // Don't load data if draft is incomplete
    if (!draft.barber_user_id || !draft.service_id || !draft.start_local) {
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load barber and service data
        const [barbersResponse, servicesResponse] = publicBookingId
          ? [await axios.get(`/public/booking/${publicBookingId}`), null]
          : await Promise.all([
              axios.get('/portal/barbers'),
              axios.get('/portal/services')
            ]);
        
        // Check if responses are valid
        if (publicBookingId && !Array.isArray(barbersResponse.data?.barbers)) {
          console.error('Invalid public booking response:', barbersResponse);
          toast.error('Failed to load booking information. Please try again.');
          navigate('/app/book');
          return;
        }

        if (!publicBookingId && (!barbersResponse.data?.ok || !servicesResponse.data?.ok)) {
          console.error('Invalid API responses:', { barbersResponse, servicesResponse });
          toast.error('Failed to load booking information. Please try again.');
          navigate('/app/book');
          return;
        }
        
        const publicBarbers = publicBookingId ? (barbersResponse.data?.barbers || []) : barbersResponse.data.barbers;
        const publicServices = publicBookingId ? (barbersResponse.data?.services || []) : servicesResponse.data.services;

        const selectedBarber = publicBarbers.find(b => b.id === draft.barber_user_id);
        const selectedService = publicServices.find(s => s.id === draft.service_id);
        
        if (!selectedBarber || !selectedService) {
          console.error('Barber or service not found:', { 
            selectedBarber, 
            selectedService, 
            barberId: draft.barber_user_id, 
            serviceId: draft.service_id,
            availableBarbers: barbersResponse.data.barbers,
            availableServices: servicesResponse.data.services
          });
          toast.error('Selected barber or service is no longer available');
          navigate('/app/book');
          return;
        }
        
        setBarber(selectedBarber);
        setService(selectedService);
        
        // Load salon settings (assuming salon_id = 1 for now)
        if (publicBookingId) {
          const availability = barbersResponse.data?.availability || {};
          setSalonSettings({
            client_cancel_lead_min: availability.client_cancel_lead_min || 120,
            client_reschedule_lead_min: availability.client_reschedule_lead_min || 120,
            auto_confirm: availability.auto_confirm ?? true,
            timezone: availability.timezone
          });
        } else {
          try {
            const settingsResponse = await axios.get('/api/salons/1/settings/client');
            setSalonSettings(settingsResponse.data);
          } catch (error) {
            console.warn('Failed to load salon settings:', error);
            // Use default values if settings can't be loaded
            setSalonSettings({
              client_cancel_lead_min: 120,
              client_reschedule_lead_min: 120,
              auto_confirm: true
            });
          }
        }
        
      } catch (error) {
        console.error('Failed to load booking data:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          barberId: draft.barber_user_id,
          serviceId: draft.service_id
        });
        
        // Check if it's an authentication error
        if (!publicBookingId && (error.response?.status === 401 || error.response?.status === 403)) {
          toast.error('Please log in again to continue');
          navigate('/?auth=1');
        } else {
          toast.error('Failed to load booking information. Please try again.');
          navigate('/app/book');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [draft.barber_user_id, draft.service_id, draft.start_local, navigate, publicBookingId]);

  const formatDateTime = (dateTimeStr) => {
    const salonTimezone = draft.timezone || salonSettings?.timezone || localStorage.getItem('shopTimezone') || 'UTC';
    const date = new Date(dateTimeStr);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: salonTimezone });
    const dateStr = date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short',
      timeZone: salonTimezone
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: salonTimezone
    });
    
    return {
      primary: `${weekday}, ${dateStr} at ${timeStr}`,
      salonTimezone
    };
  };

  const formatLeadTime = (minutes) => {
    if (!minutes || isNaN(minutes)) return '2 hours'; // Default fallback
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour';
    return `${hours} hours`;
  };

  const handleSubmit = async () => {
    if (!barber || !service || !draft.start_local) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      let response;
      
      if (publicBookingId && draft.reschedule_booking_id) {
        toast.error('Rescheduling requires login.');
        return;
      }

      if (draft.reschedule_booking_id) {
        // Reschedule existing booking
        response = await axios.post(`/portal/bookings/${draft.reschedule_booking_id}/reschedule`, {
          start_local: draft.start_local
        });
        
        // Fire analytics event
        if (window.gtag) {
          window.gtag('event', 'booking_rescheduled', {
            booking_id: draft.reschedule_booking_id,
            barber_id: draft.barber_user_id,
            service_id: draft.service_id,
            new_time: draft.start_local
          });
        }
        
        toast.success('Booking rescheduled successfully');
        
        // Trigger dashboard refresh for admin and barber dashboards
        window.dispatchEvent(new CustomEvent('bookingRescheduled', {
          detail: {
            booking_id: draft.reschedule_booking_id,
            barber_id: draft.barber_user_id,
            service_id: draft.service_id,
            new_time: draft.start_local
          }
        }));
        
        // Navigate back to bookings page
        navigate('/app/bookings');
        
      } else {
        // Create new booking
        const bookingData = {
          barber_user_id: draft.barber_user_id,
          service_id: draft.service_id,
          start_local: draft.start_local,
          notes: notes.trim()
        };
        
        console.log('Sending booking data:', bookingData);
        console.log('start_local value:', draft.start_local);
        console.log('start_local type:', typeof draft.start_local);
        
        if (publicBookingId) {
          if (!clientName.trim() || !clientEmail.trim()) {
            setError('Name and email are required to book.');
            toast.error('Name and email are required to book.');
            setIsSubmitting(false);
            return;
          }
          response = await axios.post(`/public/booking/${publicBookingId}`, {
            ...bookingData,
            client_name: clientName.trim(),
            client_email: clientEmail.trim(),
            client_phone: clientPhone.trim()
          });
        } else {
          response = await axios.post('/portal/bookings', bookingData);
        }
        
        if (response.data.ok) {
          // Clear the draft
          clearDraft();
          
          // Fire analytics event
          if (window.gtag) {
            window.gtag('event', 'booking_created', {
              barber_id: draft.barber_user_id,
              service_id: draft.service_id,
              booking_id: response.data.booking.id
            });
          }
          
          // Trigger dashboard refresh for admin and barber dashboards
          window.dispatchEvent(new CustomEvent('bookingCreated', {
            detail: {
              booking: response.data.booking,
              barber_id: draft.barber_user_id,
              service_id: draft.service_id
            }
          }));
          
          // Show confirmation popup before navigating to success page
          setConfirmationPayload({
            booking: response.data.booking,
            barber,
            service,
            appointmentTime: draft.start_local,
            timezone: response.data.booking?.timezone || draft.timezone || salonSettings?.timezone || 'UTC'
          });
          setShowBookingConfirmation(true);
        }
      }
      
    } catch (error) {
      console.error('Failed to process booking:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      
      // Handle specific error codes
      const errorCode = error.response?.data?.error;
      let errorMessage = draft.reschedule_booking_id ? 'Failed to reschedule booking. Please try again.' : 'Failed to create booking. Please try again.';
      
      switch (errorCode) {
        case 'EMAIL_ALREADY_REGISTERED':
          errorMessage =
            'This email is already registered as a staff account. Please use a different email to book, or create the booking from the Admin panel.';
          break;
        case 'BOOKING_CONFLICT':
          // Silent refresh: return client to time selection without showing an error
          setError(null);
          setRetryCount(0);
          navigate('/app/book/time');
          return;
        case 'OUTSIDE_AVAILABILITY':
          errorMessage = 'Selected time is outside working hours.';
          break;
        case 'INVALID_SLOT_ALIGNMENT':
          errorMessage = 'Please choose a valid time from the list.';
          break;
        case 'LEAD_TIME_NOT_MET':
          errorMessage = 'Too soon to book this time.';
          break;
        case 'MAX_ADVANCE_EXCEEDED':
          errorMessage = 'Too far in the future.';
          break;
        case 'PAST_TIME':
          errorMessage = 'This time has passed.';
          break;
        case 'BARBER_NOT_ELIGIBLE':
          errorMessage = 'This barber doesn\'t perform that service.';
          break;
        case 'CLIENT_RESCHEDULE_WINDOW':
          errorMessage = `You can only reschedule bookings more than ${Math.floor((salonSettings?.client_reschedule_lead_min || 120) / 60)} hours in advance`;
          break;
        case 'BOOKING_NOT_RESCHEDULABLE':
          errorMessage = 'This booking cannot be rescheduled';
          break;
        default:
          errorMessage = error.response?.data?.error || errorMessage;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToTime = () => {
    navigate('/app/book/time');
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="animate-pulse">
            <div className="h-8 w-64 bg-zinc-300 dark:bg-zinc-600 rounded-lg mb-2"></div>
            <div className="h-4 w-48 bg-zinc-300 dark:bg-zinc-600 rounded-lg"></div>
          </div>
        </div>
        
        <Card className="p-6">
          <div className="animate-pulse">
            <div className="h-6 w-32 bg-zinc-300 dark:bg-zinc-600 rounded-lg mb-4"></div>
            <div className="space-y-4">
              <div className="h-20 bg-zinc-300 dark:bg-zinc-600 rounded-xl"></div>
              <div className="h-20 bg-zinc-300 dark:bg-zinc-600 rounded-xl"></div>
              <div className="h-20 bg-zinc-300 dark:bg-zinc-600 rounded-xl"></div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!barber || !service) {
    return (
      <div className="max-w-6xl mx-auto">
        <Card className="p-8">
          <div className={`text-center py-8 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
            <svg className="h-16 w-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-medium mb-2 text-zinc-900 dark:text-zinc-100">{t('booking.not_found_title')}</h3>
            <p className="mb-6">{t('booking.not_found_subtitle')}</p>
            <motion.button
              onClick={() => navigate('/app/book')}
              className="px-8 py-3 rounded-xl font-medium text-white shadow-lg transition-all duration-200"
              style={{ 
                background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`,
                boxShadow: `0 4px 16px ${ACCENT}40`
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('booking.start_new_booking')}
            </motion.button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {draft.reschedule_booking_id ? t('booking.reschedule_title') : t('booking.review_title')}
        </h1>
        <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {draft.reschedule_booking_id ? t('booking.reschedule_subtitle') : t('booking.step3_subtitle')}
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <motion.div 
          className={`mb-6 p-4 rounded-2xl border ${
            isDark ? 'bg-red-900/20 border-red-700/50 text-red-300' : 'bg-red-50/80 border-red-200 text-red-700'
          } backdrop-blur-sm`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
            {error.includes('taken by another customer') && retryCount === 0 && (
              <motion.button
                onClick={() => {
                  setError(null);
                  setRetryCount(1);
                  handleSubmit();
                }}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  isDark 
                    ? 'bg-red-700 hover:bg-red-600 text-red-100' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Try Again
              </motion.button>
            )}
          </div>
          <div className="mt-2">
            <button
              onClick={handleBackToTime}
              className={`text-sm underline hover:no-underline ${
                isDark ? 'text-red-200' : 'text-red-600'
              }`}
            >
              {t('booking.change_time_link')}
            </button>
          </div>
        </motion.div>
      )}

      {/* Main Content Grid - Summary and Notes Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Booking Summary */}
        <Card className="p-6">
          <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            {t('booking.appointment_summary')}
          </h2>
          
          <div className="space-y-6">
            {/* Barber */}
            <motion.div 
              className="flex items-center space-x-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {barber.avatar_url ? (
                <div
                  className="w-14 h-14 rounded-2xl overflow-hidden shadow-lg flex-shrink-0"
                  style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}
                >
                  <ProfileAvatar
                    name={barber.name}
                    avatarUrl={barber.avatar_url}
                    size="lg"
                    className="rounded-2xl w-full h-full"
                    previewable
                  />
                </div>
              ) : (
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${barber.color_hex || ACCENT} 0%, ${barber.color_hex || ACCENT}CC 100%)`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                  }}
                >
                  {barber.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  {barber.name}
                </h3>
                <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  {t('booking.your_barber')}
                </p>
              </div>
            </motion.div>

            {/* Service */}
            <motion.div 
              className="flex items-start space-x-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                isDark ? 'bg-zinc-800/50' : 'bg-white/50'
              } backdrop-blur-sm`}>
                <svg className="h-7 w-7 text-zinc-600 dark:text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  {service.name}
                </h3>
                <p className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  {service.duration_min} minutes
                </p>
                {service.description && (
                  <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    {service.description}
                  </p>
                )}
              </div>
            </motion.div>

            {/* Date & Time */}
            <motion.div 
              className="flex items-start space-x-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                isDark ? 'bg-zinc-800/50' : 'bg-white/50'
              } backdrop-blur-sm`}>
                <svg className="h-7 w-7 text-zinc-600 dark:text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                {(() => {
                  const bookingTime = formatDateTime(draft.start_local);
                  return (
                    <>
                      <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                        {bookingTime.primary}
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
                        {bookingTime.salonTimezone}
                      </p>
                    </>
                  );
                })()}
              </div>
            </motion.div>
          </div>
        </Card>

        <div className="space-y-6">
          {publicBookingId && (
            <Card className="p-6">
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                {t('booking.your_details')}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('booking.full_name_label')}
                  </label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder={t('booking.client_name')}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                      isDark
                        ? 'bg-zinc-800/50 border-white/10 text-white placeholder-zinc-400 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
                        : 'bg-white/50 border-white/20 text-zinc-900 placeholder-zinc-500 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
                    }`}
                    style={{ '--accent': ACCENT }}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('booking.email_label')}
                  </label>
                  <input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="you@email.com"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                      isDark
                        ? 'bg-zinc-800/50 border-white/10 text-white placeholder-zinc-400 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
                        : 'bg-white/50 border-white/20 text-zinc-900 placeholder-zinc-500 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
                    }`}
                    style={{ '--accent': ACCENT }}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('booking.phone_optional')}
                  </label>
                  <input
                    type="tel"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                      isDark
                        ? 'bg-zinc-800/50 border-white/10 text-white placeholder-zinc-400 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
                        : 'bg-white/50 border-white/20 text-zinc-900 placeholder-zinc-500 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
                    }`}
                    style={{ '--accent': ACCENT }}
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Notes */}
          <Card className="p-6">
            <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              {t('booking.additional_notes')}
            </h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('booking.notes_placeholder')}
              maxLength={1000}
              rows={8}
              className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 resize-none backdrop-blur-sm ${
                isDark
                  ? 'bg-zinc-800/50 border-white/10 text-white placeholder-zinc-400 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
                  : 'bg-white/50 border-white/20 text-zinc-900 placeholder-zinc-500 focus:border-[--accent]/50 focus:ring-2 focus:ring-[--accent]/20'
              }`}
              style={{ '--accent': ACCENT }}
            />
            <div className={`mt-2 text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              {notes.length}/1000 characters
            </div>
          </Card>
        </div>
      </div>

      {/* Policy Information */}
      {salonSettings && (
        <motion.div 
          className={`p-5 rounded-2xl border mb-6 backdrop-blur-sm ${
            isDark ? 'bg-[rgba(125,102,51,.15)] border-[rgba(125,102,51,.35)]' : 'bg-amber-50/80 border-amber-200'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-xl ${
              isDark ? 'bg-amber-400/20' : 'bg-amber-100'
            }`}>
              <svg className={`h-5 w-5 ${
                isDark ? 'text-amber-300' : 'text-amber-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className={`font-semibold mb-2 ${
                isDark ? 'text-amber-200' : 'text-amber-800'
              }`}>
                {t('booking.booking_policy')}
              </h3>
              <div className={`text-sm space-y-1 ${
                isDark ? 'text-amber-200/90' : 'text-amber-700'
              }`}>
                <p>• {t('booking.cancellation_policy', { time: formatLeadTime(salonSettings?.client_cancel_lead_min) })}</p>
                <p>• {t('booking.reminders_policy')}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <motion.button
          onClick={handleBackToTime}
          className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
            isDark 
              ? 'bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-300 border border-white/10' 
              : 'bg-white/50 hover:bg-white/70 text-zinc-600 border border-white/20'
          } backdrop-blur-sm`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {t('booking.back_to_time')}
        </motion.button>

        <motion.button
          onClick={handleSubmit}
          disabled={isSubmitting || showBookingConfirmation}
          className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
            isSubmitting
              ? 'bg-zinc-300 dark:bg-zinc-600 text-zinc-500 dark:text-zinc-400 cursor-not-allowed'
              : 'text-white shadow-lg'
          }`}
          style={!isSubmitting ? { 
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`,
            boxShadow: `0 4px 16px ${ACCENT}40`
          } : {}}
          whileHover={!isSubmitting ? { scale: 1.02 } : {}}
          whileTap={!isSubmitting ? { scale: 0.98 } : {}}
        >
          {isSubmitting
            ? (draft.reschedule_booking_id ? t('booking.rescheduling') : t('booking.creating_booking'))
            : (draft.reschedule_booking_id ? t('booking.reschedule_btn') : t('booking.book_now'))
          }
        </motion.button>
      </div>

      {/* Booking Confirmation Modal */}
      {showBookingConfirmation && confirmationPayload && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full"
          >
            <Card className="p-8 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isDark ? 'bg-green-900/20' : 'bg-green-100'
              }`}>
                <svg className={`w-8 h-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className={`text-2xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                {t('booking.modal_booked_title')}
              </h2>
              <p className={`${isDark ? 'text-zinc-300' : 'text-zinc-600'} mb-6`}>
                {t('booking.modal_booked_subtitle')}
              </p>
              <button
                onClick={() => {
                  setShowBookingConfirmation(false);
                  navigate('/app/book/success', { state: confirmationPayload });
                }}
                className="w-full py-3 px-6 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg"
                style={{ backgroundColor: ACCENT }}
              >
                {t('booking.view_booking_details')}
              </button>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default BookingReviewPage;
